/*
 *  Copyright (c) Qwoach LLC 2020. All rights reserved.
 *  Unauthorized copying of this file, via any medium is strictly prohibited
 *  Proprietary and confidential
 *  Created by Artem Bogush <artem@qwoach.com> on 7/10/2020
 */

import ScheduledEvent from "@/core/js/model/scheduler/ScheduledEvent";
import AvailabilitySettings from "@/core/js/model/scheduler/AvailabilitySettings";
import {Type} from "class-transformer";
import {classToClass} from "@qwoach/class-transformer-wrapper";
import {EventQuestion} from "@/core/js/model/scheduler/EventQuestion";
import {TimeInterval} from "@/core/js/model/scheduler/TimeInterval";
import {LocationItem} from "@/core/js/model/scheduler/LocationItem";

import {FormTypeEnum} from "@/core/modules/coach/Resources/Forms/model/FormTypeEnum";
import {TimeSource} from "@shared/scheduler/TimeSource";
import {PaletteEnum} from "@/core/js/PaletteEnum";
import CurrencyItem from "@shared/Profile/CurrencyItem";
import BillingData, {BillingType} from "@shared/Billing/BillingData";
import BillingItem from "@shared/Billing/BillingItem";
import InvoiceItemOld from "@shared/Invoice/InvoiceItemOld";
import SendingReminderWrapper from "@shared/scheduler/reminders/SendingReminderWrapper";
import FilterableItem from "@shared/Filterable/FilterableItem";
import {FilterableProperty} from "@shared/Filterable/PropertyDecorators";

import dayjs from "dayjs";
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import {EmailReminderItem} from "@shared/scheduler/reminders/reminderItem-imports";
dayjs.extend(utc)
dayjs.extend(timezone)


export const EVENT_TYPE_DEFAULT_ID = "default-event-type-id";

export const EventTypeCategory = Object.freeze({
	SCHEDULER: "SCHEDULER",
	PROGRAM: "PROGRAM",
	TEMPLATE: "TEMPLATE",
})

export default class EventType extends FilterableItem{

	@FilterableProperty()
	displayName: string = "";

	@Type(() => AvailabilitySettings)
	availabilitySettings: ?AvailabilitySettings = null;
	timeSlots: number[] = [];
	timeSource : string = TimeSource.SCHEDULER
	isAvailabilityLocal: boolean = false; //as opposite to global availability
	description: string = null;
	// goal: string = "";
	displayId: string = "";
	fullUrl: string = ""
	isDisabled: boolean = false
	@Type(() => EventQuestion)
	questions: EventQuestion[] = [];

	@Type(() => LocationItem)
	locations: LocationItem[] = [];

	category: string = EventTypeCategory.SCHEDULER;
	coachId: string = ""
	teamMemberId: string = null
	ownerId: string = null

	coachName: string = ""
	coachEmail: string = ""
	coachPictureUrl: string = ""
	brandLogoUrl: string = ""
	coachTz: string = ""
	coachTzName: string = ""

	eventColor: string = PaletteEnum.COLOR_PICKER_PRIMARY

	paidSession: boolean = false
	price: number = 0
	paymentTerms: string = ""

	@Type(() => CurrencyItem)
	currencyItem: CurrencyItem = CurrencyItem.USD

	isCancellationPolicyEnabled: boolean = false
	minimumCancellationNotice: number = 24 * (60 * 60 * 1000)
	cancellationPolicyDescription: string = null

	increments: number = 30 * (60 * 1000);
	maxEventsPerDay: number = 0;
	maxEventsPerWeek: number = 0;
	minSchedulingNotice: number = 24 * (60 * 60 * 1000);
	eventBufferBefore: number = 0 * (60 * 1000);
	eventBufferAfter: number = 30 * (60 * 1000);

	duration: number = 30 * (60 * 1000);
	rollingDays: number = 30 * (24 * 60 * 60 * 1000);

	isEmailReminderEnabled: boolean = false
	isEmailReminderEnabledCanEdit: boolean = true

	@Type(() => EmailReminderItem)
	emailReminders: EmailReminderItem[] = [new EmailReminderItem(undefined, undefined, "Email reminder 1")]

	@Type(() => SendingReminderWrapper)
	sendingReminderWrapper: SendingReminderWrapper[] = []

	// @Type(() => EmailReminderItem)
	// emailReminders: EmailReminderItem[] = [new EmailReminderItem(null, null, "Email reminder 1")]

	intakeFormId: string = null;
	// prepFormId: string = null;
	// postFormId: string = null;
	isIntakeFormEnabled: boolean = false
	// isPrepFormEnabled: boolean = false
	// isPostFormEnabled: boolean = false

	attachedWorkflowIds: string[] = []

	needsSetup: boolean = true

	constructor(availabilitySettings = null, displayName = null) {
		super();
		if(availabilitySettings){
			this.availabilitySettings = availabilitySettings;
		}

		this.upgradeRemindersIfNeeded()
		this.displayName = displayName
		// this.questions = [
		// 	new EventQuestion("Full Name", QuestionTypeEnum.ONE_LINE, true, true),
		// 	new EventQuestion("Email", QuestionTypeEnum.ONE_LINE, true, true),
		// ];

	}

	upgradeRemindersIfNeeded(){
		console.log("upgradeRemindersIfNeeded: ", this.emailReminder)
		if(this.emailReminder){
			this.emailReminder.title = "Email reminder 1"
			this.emailReminder.subject = "Email reminder 1"
			this.emailReminder.trigger.recalculateShiftParams()
			this.emailReminders = [this.emailReminder]
			this.emailReminder = null
		}
	}

	subtractBusyIntervals(busyIntervals: TimeInterval[], availabilitySettings = this.availabilitySettings): AvailabilitySettings{
		return availabilitySettings.subtractBusyIntervals(busyIntervals, this)
	}

	transposeToCurrentTimezone(dateStart, dateEnd, userTimezone){
		let timezoneDifference = 0
		if(userTimezone){
			let coachOffset = dayjs().tz(this.coachTz).utcOffset()
			let clientOffset = dayjs().tz(userTimezone).utcOffset()
			timezoneDifference = (clientOffset - coachOffset) * 60 * 1000
			console.log("loadAvailabilityForMonth: coachOffset = ", coachOffset, ", clientOffset = ", clientOffset, ", userTimezone = ", userTimezone, ", timezoneShift = ", timezoneDifference)
		}
		console.log("loadAvailabilityForMonth: userTimezone = ", userTimezone, ", timezoneShift = ", timezoneDifference)


		return this.availabilitySettings.transposeToCurrentTimezone(dateStart, dateEnd, this, timezoneDifference)
	}

	isTimeIntervalAvailable(timeInterval, userTimezoneId){
		let dayStart = dayjs(timeInterval.startTime).startOf('day').valueOf()
		let dayEnd = dayjs(timeInterval.endTime).endOf('day').valueOf()
		// we call transposeToCurrentTimezone even if userTimezoneId is not provided to get the correct availability for the day
		// since it calculates 3 days availability for each day (yesterday+today+tomorrow) to account for intervals that span 2 days.
		let availabilitySettings = this.transposeToCurrentTimezone(dayStart, dayEnd, userTimezoneId)


		console.log("isTimeIntervalAvailable: transposedAvailability = ", availabilitySettings)
		return availabilitySettings.isTimeIntervalAvailable(timeInterval, this)
	}


	isCategoryProgram(){
		return this.category === EventTypeCategory.PROGRAM
	}

	rollingDaysMsToDays(){
		if(this.rollingDays > 0 && this.rollingDays < 100){
			//old format was days - convert it to ms
			this.rollingDays = this.rollingDays * (24 * 60 * 60 * 1000)
		}
		return Math.floor(this.rollingDays / (24 * 60 * 60 * 1000))
	}
	minSchedulingNoticeHours(){
		return Math.floor(this.minSchedulingNotice / (60 * 60 * 1000))
	}
	minimumCancellationNoticeHours(){
		return this.minimumCancellationNotice / (60 * 60 * 1000)
	}

	durationMinutes(){
		return this.duration / (60 * 1000)
	}
	bufferAfterMinutes(){
		return this.eventBufferAfter / (60 * 1000)
	}
	bufferBeforeMinutes(){
		return this.eventBufferBefore / (60 * 1000)
	}
	incrementMinutes(){
		return this.increments / (60 * 1000)
	}

	getSessionTypeRouterOptions(schedulerSettings, query = {}){
		return {
			name: 'MainMeetingSchedulePage',
			params:{
				eventTypeUserId: schedulerSettings.eventTypeUserId,
				eventTypeId: this.displayId
			},
			query: query
		}
	}


	getLastBookableDate(){
		let day = new Date();
		let date = new Date(
			day.getFullYear(),
			day.getMonth(),
			day.getDate() + this.rollingDays
		)
		return date;
	}

	isPaidSession(){
		return this.paidSession && this.price > 0
	}


	createScheduledSession(eventOrigin, clientProfile){
		console.log("createScheduledSession: eventOrigin = ", eventOrigin)
		let event = eventOrigin ? classToClass(eventOrigin) : new ScheduledEvent()
		event.fillFromSessionType(this, clientProfile)
		return event;
	}


	isAnyFormSet(){
		return !!this.intakeFormId || !!this.prepFormId || !!this.postFormId
	}


	formTitleByType(formType){
		let formName = ""
		switch (formType){
			case FormTypeEnum.INTAKE_FORM:
				formName = "Intake Form";
				break;
			case FormTypeEnum.PREP_FORM:
				formName = "Prep Form";
				break;
			case FormTypeEnum.POST_FORM:
				formName = "Post Session Form"
				break;
		}
		return `${this.displayName}  (${formName})`
	}

	priceFormatted(currencyItem){
		currencyItem = currencyItem || this.currencyItem
		return currencyItem.formatted(this.price)
	}

	createInvoice(billFromCoachId, billToClientId){
		let billingData = new BillingData()
		billingData.billingType = BillingType.FIXED
		billingData.billingItems.push(new BillingItem(this.price, "Session: " + this.displayName))
		return InvoiceItemOld.instanceWithFromTo(billingData, billFromCoachId, billToClientId, this.currencyItem)
	}

	// Backward compatibility for callers that still use the old factory name.
	createInvoiceItem(billFromCoachId, billToClientId){
		return this.createInvoice(billFromCoachId, billToClientId)
	}


	getAttachedFileRefListForReminder(reminder){
		console.log("EventType.getAttachedFileRefListForReminder: reminder = ", reminder, ", this.sendingReminderWrapper = ", this.sendingReminderWrapper)
		if(!reminder){
			return []
		}
		for(let reminderWrapper of this.sendingReminderWrapper){
			if(reminderWrapper.initialReminder
				&& reminderWrapper.initialReminder.id === reminder.id){
				return reminderWrapper.getAllFilesRefs()
			}
		}
		return []
	}

	static isDefaultId(eventTypeId) {
		return eventTypeId === EVENT_TYPE_DEFAULT_ID
	}
	static isDefault(eventType = {}) {
		return eventType.id === EVENT_TYPE_DEFAULT_ID || eventType.displayId === EVENT_TYPE_DEFAULT_ID
	}

	static createDefaultEventType(options = {}) {

		let {coachId = null, ownerId = null, displayName = "Session", displayId = EVENT_TYPE_DEFAULT_ID} = options

		let eventType = new EventType()
		eventType.displayName = displayName
		eventType.displayId = displayId
		eventType.coachId = coachId
		eventType.ownerId = ownerId || coachId
		return eventType;
	}


}
